/**
 * Reusable citation UI for the AI Diagnostic Knowledge Retrieval system.
 *
 * `<CitationCard/>` — renders a single retrieved citation with source
 * metadata, an inline score-factor breakdown, and a "View Source" trigger
 * that opens the full-context popover.
 *
 * `<ViewSourceModal/>` — shown when the technician clicks "View Source"
 * on any citation. Loads the parent chunk + neighbor chunks + full doc
 * metadata, and links to download the original file.
 *
 * These are trade-agnostic — no HVAC/plumbing/electrical language. Both
 * are used by the admin Retrieval Tester today and are ready to plug into
 * the Guru chat surface later.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BookOpen,
  Calendar,
  Download,
  ExternalLink,
  FileText,
  Hash,
  Info,
  Loader2,
  Tag,
  X,
  Zap,
} from "lucide-react";
import { kbApi } from "../lib/api";

const DOC_TYPE_LABEL = {
  service_manual: "Service manual",
  repair_manual: "Repair manual",
  installation_manual: "Installation manual",
  troubleshooting_guide: "Troubleshooting guide",
  technical_bulletin: "Technical bulletin",
  specification: "Specification",
  wiring_diagram: "Wiring diagram",
  schematic: "Schematic",
  error_code_doc: "Error-code doc",
  parts_doc: "Parts doc",
  diagnostic_procedure: "Diagnostic procedure",
  maintenance_procedure: "Maintenance procedure",
  training_doc: "Training doc",
  manual: "Manual",
};

const TIER_LABEL = {
  model: "Exact model",
  family: "Model family",
  manufacturer: "Manufacturer",
  equipment: "Equipment type",
  category: "Service category",
  global: "Global doc",
};

const TIER_COLOR = {
  model: "bg-emerald-100 text-emerald-800",
  family: "bg-teal-100 text-teal-800",
  manufacturer: "bg-blue-100 text-blue-800",
  equipment: "bg-indigo-100 text-indigo-800",
  category: "bg-violet-100 text-violet-800",
  global: "bg-slate-100 text-slate-600",
};

// -----------------------------------------------------------------------------
export function CitationCard({ citation, rank, onViewSource, compact = false }) {
  const factors = citation.scoreFactors || {};
  const tier = factors.scope?.tier || "global";
  return (
    <div
      className="rounded-md border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all"
      data-testid={`kb-citation-${citation.chunkId}`}
    >
      <div className="px-3 py-2.5 border-b border-slate-100 flex items-start gap-2">
        <div className="h-6 w-6 shrink-0 rounded-md bg-blue-50 text-blue-700 text-[11px] font-semibold flex items-center justify-center">
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onViewSource?.(citation)}
            className="text-left w-full group"
            data-testid={`kb-citation-view-source-${citation.chunkId}`}
          >
            <div className="text-[13px] font-medium text-slate-900 group-hover:text-blue-700 truncate flex items-center gap-1.5">
              {citation.documentTitle || citation.documentFilename || "Untitled document"}
              <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>
            <div className="text-[11px] text-slate-500 flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
              <span>{DOC_TYPE_LABEL[citation.docType] || citation.docType}</span>
              {citation.docVersion && (
                <span className="flex items-center gap-0.5">
                  <Hash className="h-2.5 w-2.5" /> {citation.docVersion}
                </span>
              )}
              {citation.docEffectiveDate && (
                <span className="flex items-center gap-0.5">
                  <Calendar className="h-2.5 w-2.5" /> {citation.docEffectiveDate}
                </span>
              )}
              {citation.page != null && citation.page > 0 && (
                <span>· Page {citation.page}</span>
              )}
              {citation.section && (
                <span className="truncate max-w-[220px]">· {citation.section}</span>
              )}
            </div>
          </button>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${TIER_COLOR[tier] || TIER_COLOR.global}`}
            title={`Retrieval tier: ${TIER_LABEL[tier] || tier}`}
          >
            {TIER_LABEL[tier] || tier}
          </span>
          <span className="text-[10.5px] text-slate-500" title="Total blended score">
            score {citation.score}
          </span>
        </div>
      </div>

      {!compact && (
        <>
          <div className="px-3 py-2 text-[12.5px] text-slate-700 whitespace-pre-wrap break-words border-b border-slate-50 max-h-40 overflow-y-auto">
            {citation.text}
          </div>
          <div className="px-3 py-1.5 flex flex-wrap items-center gap-1">
            {citation.sectionType && citation.sectionType !== "general" && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700">
                {citation.sectionType}
              </span>
            )}
            {(factors.errorCode?.matched || []).map((code) => (
              <span key={code} className="px-1.5 py-0.5 rounded text-[10px] bg-rose-50 text-rose-700 flex items-center gap-0.5">
                <Zap className="h-2.5 w-2.5" /> {code}
              </span>
            ))}
            {(factors.component?.matched || []).slice(0, 3).map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700">
                {c}
              </span>
            ))}
            {factors.freshness > 0.5 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-800">
                fresh +{factors.freshness}
              </span>
            )}
            <ScoreBreakdown factors={factors} />
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function ScoreBreakdown({ factors }) {
  const [open, setOpen] = useState(false);
  const rows = [
    ["BM25 relevance", factors.bm25],
    ["Scope tier", factors.scope?.boost],
    ["Doc type", factors.docType?.boost],
    ["Section", factors.sectionType?.boost],
    ["Freshness", factors.freshness],
    ["Error-code match", factors.errorCode?.boost],
    ["Component match", factors.component?.boost],
  ].filter(([, v]) => v && v > 0);
  if (rows.length === 0) return null;
  return (
    <div className="relative ml-auto">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[10.5px] text-slate-400 hover:text-slate-700 flex items-center gap-0.5"
        data-testid="kb-citation-score-toggle"
      >
        <Info className="h-3 w-3" /> Why?
      </button>
      {open && (
        <div className="absolute right-0 bottom-6 z-20 w-[220px] rounded-md border border-slate-200 bg-white p-2 shadow-lg text-[11px]">
          {rows.map(([label, v]) => (
            <div key={label} className="flex items-center justify-between text-slate-700">
              <span>{label}</span>
              <span className="font-mono text-slate-800">+{Number(v).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
export function ViewSourceModal({ citation, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!citation) return;
    setLoading(true);
    kbApi
      .citation(citation.chunkId, 2)
      .then(setData)
      .catch(() => toast.error("Could not load source material"))
      .finally(() => setLoading(false));
  }, [citation]);

  if (!citation) return null;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl max-h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="flex items-start gap-3 min-w-0 pr-4">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-slate-900 truncate" data-testid="kb-view-source-title">
                {data?.document?.title || citation.documentTitle}
              </div>
              <div className="text-[11.5px] text-slate-500 flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                <span>{DOC_TYPE_LABEL[data?.document?.docType] || data?.document?.docType}</span>
                {data?.document?.version && <span>· v{data.document.version}</span>}
                {data?.document?.effectiveDate && (
                  <span className="flex items-center gap-0.5">
                    <Calendar className="h-2.5 w-2.5" /> {data.document.effectiveDate}
                  </span>
                )}
                {data?.document?.source && <span>· via {data.document.source}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {data?.document?.fileUrl && (
              <a
                href={kbApi.documents.downloadUrl(data.document.id)}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] text-blue-700 hover:underline flex items-center gap-1"
                data-testid="kb-view-source-download"
              >
                <Download className="h-3.5 w-3.5" /> Download original
              </a>
            )}
            <button
              className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
              onClick={onClose}
              data-testid="kb-view-source-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !data ? (
            <div className="text-center text-slate-400 py-8">Source unavailable.</div>
          ) : (
            <>
              <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-2">
                <Tag className="h-3 w-3" />
                <span>Page {data.chunk.page}</span>
                {data.chunk.section && <span>· {data.chunk.section}</span>}
                <span className="ml-auto text-slate-400">
                  Showing {data.neighbors.length} chunk{data.neighbors.length === 1 ? "" : "s"} in context
                </span>
              </div>
              {data.neighbors.map((n) => {
                const highlighted = n.id === data.chunk.id;
                return (
                  <div
                    key={n.id}
                    className={`rounded-md p-3 border ${
                      highlighted ? "border-blue-400 bg-blue-50/40" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="text-[10.5px] text-slate-500 flex items-center gap-2 mb-1.5">
                      <FileText className="h-3 w-3" />
                      <span>{n.section || "—"}</span>
                      <span className="text-slate-400">·</span>
                      <span>{n.sectionType}</span>
                      {(n.errorCodes || []).map((code) => (
                        <span key={code} className="px-1.5 py-0.5 rounded text-[10px] bg-rose-50 text-rose-700">
                          {code}
                        </span>
                      ))}
                    </div>
                    <div className="text-[12.5px] text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
